import styled from "styled-components";

import { clamp } from "@styles/utils/conversion.style";
import { colors } from "@styles/vars/colors.style";
import { mq } from "@styles/vars/media-queries.style";

export const ModelSideBySideMain = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
  background: ${colors.charcoal};
  color: ${colors.white};

  ${mq.tabletPMax} {
    overflow: visible;
  }
`;

export const ModelSideBySideWrapper = styled.div`
  position: relative;
  width: 100%;
  isolation: isolate;
`;

export const ModelSideBySideItems = styled.div`
  position: relative;
  width: 100%;
`;

export const ModelSideBySideItem = styled.section`
  --timeline-circle-size: 2.5rem;
  --timeline-circle-radius: 1.25rem;
  --timeline-circle-top: 145px;

  position: relative;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  width: 100%;

  ${mq.tabletL} {
    align-items: start;
  }

  ${mq.tabletPMax} {
    display: block;
    min-height: auto;
    padding: 3rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);

    &:last-child {
      border-bottom: 0;
    }
  }
`;

export const ModelSideBySideItemTimeline = styled.div`
  display: none;

  ${mq.tabletL} {
    position: absolute;
    inset: 0 auto 0 50%;
    z-index: 20;
    display: block;
    width: 3rem;
    transform: translateX(-50%);
    pointer-events: none;
  }
`;

export const ModelSideBySideItemLine = styled.div`
  position: absolute;
  inset: 0 auto 0 50%;
  z-index: 1;
  width: 1px;
  height: 100%;
  background: ${colors.white};
  opacity: 0.75;
  transform: translateX(-50%);
  transform-origin: top center;
  will-change: transform;
`;

export const ModelSideBySideItemCircle = styled.div`
  position: absolute;
  top: var(--timeline-circle-top);
  left: 50%;
  z-index: 2;
  width: var(--timeline-circle-size);
  height: var(--timeline-circle-size);
  overflow: hidden;
  border-radius: 50%;
  background: ${colors.charcoal};
  transform: translate(-50%, -50%);
`;

export const ModelSideBySideItemCircleProgress = styled.div`
  --circle-progress: 0;

  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    ${colors.pastelGreen} 0deg,
    ${colors.pastelGreen} calc(var(--circle-progress) * 360deg),
    transparent calc(var(--circle-progress) * 360deg),
    transparent 360deg
  );
`;

export const ModelSideBySideItemContent = styled.div`
  position: relative;
  z-index: 10;
  grid-column: ${({ $isLeft }) => ($isLeft ? "1 / 2" : "2 / 3")};
  width: 100%;
  padding: 0 ${({ $isLeft }) => ($isLeft ? "clamp(3rem, 6vw, 7rem)" :  "0")} clamp(3rem, 6vw, 7rem) ${({ $isLeft }) => ($isLeft ? "0" : "clamp(3rem, 6vw, 7rem)")};
  margin-top: 100px;
  justify-self: ${({ $isLeft }) => ($isLeft ? "end" : "start")};
  text-align: ${({ $isLeft }) => ($isLeft ? "right" : "left")};

  ${clamp("font-size", 18, 24)};


  ${mq.tabletPMax} {
    width: 100%;
    max-width: none;
    padding: 0;
    margin-top: 0;
    text-align: left;
  }
`;

export const ModelSideBySideItemTitle = styled.div`
  
  color: ${colors.pastelGreen};
  font-size: inherit;
  margin-bottom: 40px;
  
  p {
    ${clamp("font-size", 24, 64)};
    font-family: Signifier;
    line-height: 1.2;
    strong {
      font-family: National2;
      font-size: inherit;
      font-weight: 900;
      line-height: inherit;
    }
  }

  span,
  [role="img"] {
    transform-style: preserve-3d;
  }

  ${mq.tabletPMax} {
    margin-bottom: 1.5rem;
  }
  [data-title-char] {
    display: inline-block;
    white-space: pre;
    backface-visibility: hidden;
  }
`;
export const ModelSideBySideItemSubText = styled.div`
  display: block;  
  margin-bottom: 4rem;
  font-family: Signifier;
  ${mq.tabletPMax} {
    margin-bottom: 3rem;
  }
`;

export const LogosContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 40px;
  align-items: center;
  ${mq.tabletPMax} {
    flex-wrap: wrap;
    gap: 40px;
  }
  ${mq.tabletL} {
    margin-top: 80px;
    justify-content: ${({ $isLeft }) => ($isLeft ? "flex-end" : "flex-start")};
  }
  .arrow-icon {
    height: 30px;
    width: 30px;
    color: ${colors.pastelGreen};
  }
`;

export const ModelSideBySideItemText = styled.div`
  font-size: inherit;
  line-height: inherit;

  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0 0 1.5rem;
    font-size: inherit;

    &:last-child {
      margin-bottom: 0;
    }
  }

  p {
    font-family: National2;
    font-weight: 400;
    margin-bottom: 20px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${colors.pastelGreen};
    font-family: National2;
    font-weight: 700;
  }

  span,
  [role="img"] {
    transform-style: preserve-3d;
  }

  strong {
    font-size: inherit;
    font-weight: 700;
    line-height: inherit;
  }

  ${mq.tabletPMax} {
    p,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin-bottom: 1.25rem;
    }
  }

  ${mq.tabletL} {
    max-width: 48rem;
    ${({ $isLeft }) => ($isLeft ? "margin-left: auto;" : "margin-right: auto;")}
  }
`;

